import { useEffect, useCallback, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import {
  sliceKey,
  reducer,
  templateGenerationSettingsActions,
} from 'app/pages/TemplateGenerationSettingsComponent/slice';
import { watchTemplateGenerationSettingsSaga } from 'app/pages/TemplateGenerationSettingsComponent/saga';
import { selectTemplateGenerationSettings } from 'app/pages/TemplateGenerationSettingsComponent/selectors';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { selectDealerSegmentSettings } from 'app/pages/DealerSegmentSettingsComponent/selectors';
import { 
  fetchSegmentsForDealer,
  initializeTemplateGenerationSettings,
} from 'app/pages/TemplateGenerationSettingsComponent/data/templateGenerationSettingsDataService';
import { TemplateGenerationModal } from 'app/pages/TemplateGenerationSettingsComponent/components/TemplateGenerationModal';
import type { SegmentOption } from 'app/pages/TemplateGenerationSettingsComponent/components/TemplateGenerationModal';
import { TemplateGenerationTable } from 'app/components/ReusableUIComponents/TemplateGenerationTable';
import { DeleteConfirmationDialog } from 'app/components/ReusableUIComponents/DeleteConfirmationDialog';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';

import {
  AccordionComponent,
  AccordionSummaryComponent,
  AccordionDetailsComponent,
} from 'app/components/StyledComponents';

import {
  Typography,
  Button,
  Box,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

export interface FieldCondition {
  field_name: string;
  field_value: string;
}

export interface EmailTemplateSegmentConfig {
  segment_id: string;
  segment_name: string;
  allowed_message_types: string[];
  auto_approval: boolean;
  field_conditions?: FieldCondition[];
}

function getSettingsFromResponse(data: any): {
  settingsId: string | null;
  settingsDoc: any;
  emailTemplatesConfig: EmailTemplateSegmentConfig[];
} {
  const body = data?.body ?? data;
  if (!body || !Array.isArray(body) || !body[0]?.settings?.length) {
    return { settingsId: null, settingsDoc: null, emailTemplatesConfig: [] };
  }
  const settingsDoc = body[0].settings[0];
  const settingsId = settingsDoc?._id ?? null;
  const emailTemplatesConfig =
    settingsDoc?.properties?.email_templates_config ?? [];
  return { settingsId, settingsDoc, emailTemplatesConfig };
}

type TemplateGenerationSettingsComponentProps = {
  defaultExpanded?: boolean;
  /** When set, only show the configuration row(s) for this segment name. */
  filterSegmentName?: string;
  /** When true, hides the surrounding accordion shell spacing for embedding in modals. */
  embedded?: boolean;
};

export const TemplateGenerationSettingsComponent = ({
  defaultExpanded = false,
  filterSegmentName,
  embedded = false,
}: TemplateGenerationSettingsComponentProps) => {
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: watchTemplateGenerationSettingsSaga });

  const dispatch = useDispatch();
  const selectedDealer = useSelector(selectDealerSelected) || {};
  const selectedDealerId = selectedDealer.dealer || '';
  const templateGenerationState = useSelector(selectTemplateGenerationSettings);
  const { data, loading, error } = templateGenerationState;
  const dealerSegmentState = useSelector(selectDealerSegmentSettings);

  const { settingsId, settingsDoc, emailTemplatesConfig } =
    getSettingsFromResponse(data);

  const filteredConfig = useMemo(() => {
    const filter = filterSegmentName?.trim();
    if (!filter) {
      return emailTemplatesConfig.map((row, index) => ({ row, index }));
    }
    const lower = filter.toLowerCase();
    return emailTemplatesConfig
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => (row?.segment_name ?? '').toString().trim().toLowerCase() === lower);
  }, [emailTemplatesConfig, filterSegmentName]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editState, setEditState] = useState<{ edit: boolean; index: number }>({
    edit: false,
    index: -1,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number>(-1);
  const [segmentsList, setSegmentsList] = useState<SegmentOption[]>([]);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);

  // Check if response indicates an error (even with 200 status code)
  const hasError = useMemo(() => {
    if (!data) return false;
    const statusText = data?.statusText;
    const statusCode = data?.statusCode ?? data?.myAutoIQ_status_code ?? data?.errorCode;
    
    // If statusText is not "success" or status code is >= 400, it's an error
    return (statusText && statusText !== 'success') || (typeof statusCode === 'number' && statusCode >= 400);
  }, [data]);

  useEffect(() => {
    if (selectedDealerId) {
      dispatch(
        templateGenerationSettingsActions.requestTemplateGenerationSettings({
          dealershipId: selectedDealerId,
        }),
      );
    }
  }, [selectedDealerId, dispatch]);

  useEffect(() => {
    if (modalOpen && !editState.edit && selectedDealerId) {
      setSegmentsLoading(true);
      fetchSegmentsForDealer(selectedDealerId)
        .then((res: any) => {
          const list = res?.body?.segments ?? [];
          setSegmentsList(Array.isArray(list) ? list : []);
        })
        .catch(() => setSegmentsList([]))
        .finally(() => setSegmentsLoading(false));
    }
  }, [modalOpen, editState.edit, selectedDealerId]);

  const updateConfig = useCallback(
    (updatedConfig: EmailTemplateSegmentConfig[]) => {
      if (!settingsId || !settingsDoc || !selectedDealerId) return;
      const email_templates_config = updatedConfig.map(row => {
        const { applicable_stages: _legacy, ...rest } = row as EmailTemplateSegmentConfig & {
          applicable_stages?: string[];
        };
        return rest;
      });
      const payload = {
        ...settingsDoc,
        properties: {
          ...(settingsDoc.properties || {}),
          email_templates_config,
        },
      };
      dispatch(
        templateGenerationSettingsActions.requestUpdateTemplateGenerationSettings(
          {
            dealershipId: selectedDealerId,
            settingsId,
            data: payload,
          },
        ),
      );
    },
    [settingsId, settingsDoc, selectedDealerId, dispatch],
  );

  const handleAddConfigurations = () => {
    setEditState({ edit: false, index: -1 });
    setModalOpen(true);
  };

  const handleEdit = (index: number) => {
    setEditState({ edit: true, index });
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditState({ edit: false, index: -1 });
  };

  const handleModalSave = (config: EmailTemplateSegmentConfig) => {
    const configs = [...emailTemplatesConfig];
    if (editState.edit && editState.index >= 0) {
      configs[editState.index] = config;
    } else {
      configs.push(config);
    }
    updateConfig(configs);
    handleModalClose();
  };

  const handleDeleteClick = (index: number) => {
    setDeleteIndex(index);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteIndex < 0) return;
    const configs = emailTemplatesConfig.filter((_, i) => i !== deleteIndex);
    updateConfig(configs);
    setDeleteDialogOpen(false);
    setDeleteIndex(-1);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteIndex(-1);
  };

  const handleAutoApprovalToggle = (segmentIndex: number, value: boolean) => {
    const config = [...emailTemplatesConfig];
    const segment = { ...config[segmentIndex], auto_approval: value };
    config[segmentIndex] = segment;
    updateConfig(config);
  };

  const handleInitialize = async () => {
    if (!selectedDealerId || initializing) return;
    setInitializing(true);
    try {
      const response = await initializeTemplateGenerationSettings({
        dealerId: selectedDealerId,
      });
      
      // Check if initialization was successful
      const statusText = response?.statusText;
      if (statusText === 'success') {
        dispatch(notificationAlertActions.addNotification({
          open: true,
          severity: 'success',
          message: 'Template generation settings initialized successfully',
        }));
        // Refetch data after successful initialization
        dispatch(
          templateGenerationSettingsActions.requestTemplateGenerationSettings({
            dealershipId: selectedDealerId,
          }),
        );
      } else {
        throw new Error(response?.message || 'Failed to initialize settings');
      }
    } catch (error: any) {
      dispatch(notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: error?.message || 'Failed to initialize template generation settings',
      }));
    } finally {
      setInitializing(false);
    }
  };

  // Extract dealer segments once — same parsing as DealerSegmentSettingsComponent
  const dealerSegments = useMemo<any[]>(() => {
    const dealerData = dealerSegmentState?.data;
    const body = dealerData?.data ?? dealerData?.body ?? dealerData;
    return Array.isArray(body) && body[0]?.settings?.length
      ? (body[0].settings[0]?.properties?.segments ?? [])
      : [];
  }, [dealerSegmentState]);

  // field_name → unique field_values (for autocomplete suggestions)
  const fieldValueByFieldName = useMemo(() => {
    const map: Record<string, string[]> = {};
    dealerSegments.forEach((s: any) => {
      const fn = (s.field_name ?? s.fieldName ?? '')?.toString().trim();
      const fv = (s.field_value ?? s.fieldValue ?? '')?.toString().trim();
      if (!fn) return;
      if (!map[fn]) map[fn] = [];
      if (fv && !map[fn].includes(fv)) map[fn].push(fv);
    });
    return map;
  }, [dealerSegments]);

  // primary_segment → unique field_names used (for filtering Field Name dropdown per segment)
  const fieldNamesBySegment = useMemo(() => {
    const map: Record<string, string[]> = {};
    dealerSegments.forEach((s: any) => {
      const primary = (s.primary_segment ?? s.primarySegment ?? '')?.toString().trim();
      const fn = (s.field_name ?? s.fieldName ?? '')?.toString().trim();
      if (!primary || !fn) return;
      if (!map[primary]) map[primary] = [];
      if (!map[primary].includes(fn)) map[primary].push(fn);
    });
    return map;
  }, [dealerSegments]);

  const hasData = settingsId && settingsDoc;
  
  // Show initialize button when:
  // 1. Not loading AND dealer is selected
  // 2. AND (there's an error OR no data exists OR body is empty array)
  const isEmptyResponse = data && Array.isArray(data?.body) && data.body.length === 0;
  const showInitializeButton = !loading && selectedDealerId && (hasError || !hasData || isEmptyResponse);

  return (
    <>
      <AccordionComponent
        defaultExpanded={defaultExpanded}
        elevation={0}
        sx={{
          '&::before': { display: 'none' },
          padding: embedded ? 0 : '16px',
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '5px !important',
        }}
      >
        <AccordionSummaryComponent
          expandIcon={<ExpandMoreIcon />}
          aria-controls="template-generation-content"
          id="template-generation-header"
        >
          <Typography variant="h6" gutterBottom component="div">
            Template Generation Settings
          </Typography>
        </AccordionSummaryComponent>
        <AccordionDetailsComponent>
          {loading && (
            <Typography color="textSecondary">Loading...</Typography>
          )}
          {!selectedDealerId && (
            <Typography color="textSecondary">
              Select a dealer to load template generation settings.
            </Typography>
          )}

          {showInitializeButton && (
            <>
              {hasError && data?.message && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {data.message}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {String(error)}
                </Alert>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleInitialize}
                  disabled={initializing}
                >
                  {initializing ? 'Initializing...' : 'Initialize Template Generation Settings'}
                </Button>
              </Box>
              {!hasError && !error && (
                <Typography color="textSecondary" sx={{ textAlign: 'center', mb: 2 }}>
                  No template generation config found. Click Initialize to set up.
                </Typography>
              )}
            </>
          )}

          {!loading && hasData && !hasError && (
            <>
              {!filterSegmentName ? (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddConfigurations}
                  style={{ marginTop: '25px', marginBottom: 16 }}
                >
                  Add Configurations
                </Button>
              ) : null}
              <TemplateGenerationTable
                emailTemplatesConfig={filteredConfig.map(x => x.row)}
                loading={loading}
                onEdit={(filteredIndex) => {
                  const original = filteredConfig[filteredIndex]?.index;
                  if (original == null || original < 0) return;
                  handleEdit(original);
                }}
                onDelete={(filteredIndex) => {
                  const original = filteredConfig[filteredIndex]?.index;
                  if (original == null || original < 0) return;
                  handleDeleteClick(original);
                }}
                onAutoApprovalToggle={(filteredIndex, value) => {
                  const original = filteredConfig[filteredIndex]?.index;
                  if (original == null || original < 0) return;
                  handleAutoApprovalToggle(original, value);
                }}
                ariaLabel="template generation config"
              />
            </>
          )}
        </AccordionDetailsComponent>
      </AccordionComponent>

      <TemplateGenerationModal
        open={modalOpen}
        onClose={handleModalClose}
        mode={editState.edit ? 'edit' : 'add'}
        initialValues={
          editState.edit && editState.index >= 0
            ? emailTemplatesConfig[editState.index]
            : null
        }
        segments={segmentsList}
        segmentsLoading={segmentsLoading}
        onSave={handleModalSave}
        saving={loading}
        fieldValueByFieldName={fieldValueByFieldName}
        fieldNamesBySegment={fieldNamesBySegment}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Template Generation config?"
      />
    </>
  );
};
